import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "context/OnboardingContext";
import { onboardingService } from "services/onboarding-service";
import { useMaterialUIController } from "context";
import { getOnboardingThemeTokens, withAlpha } from "./themeTokens";
import { loadStripe } from "@stripe/stripe-js";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { motion } from "framer-motion";
import OnboardingLayout from "./OnboardingLayout";
import { CircularProgress } from "@mui/material";

const BillingPage = () => {
  const navigate = useNavigate();
    const { sessionToken, subscriptionLines, claimMeta, theme, sessionId } = useOnboarding();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const t = getOnboardingThemeTokens({ darkMode, templateTheme: theme });
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initStripe = async () => {
      const stripePromise = await loadStripe(
        process.env.REACT_APP_STRIPE_PUBLIC_KEY
      );
      const stripeInstance = await stripePromise;
      setStripe(stripeInstance);

      if (stripeInstance) {
        const elements = stripeInstance.elements();
        const card = elements.create("card", {
          style: {
            base: {
              fontSize: "16px",
              color: t.textStrong,
              fontFamily: "Roboto, sans-serif",
              "::placeholder": { color: t.textMuted },
            },
          },
        });
        card.mount("#card-element");
        setCardElement(card);
      }
    };
    initStripe();
  }, []);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const resp = await onboardingService.getSetupIntent(sessionId);
      const { client_secret } = resp.data;
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (pmError) throw new Error(pmError.message);

      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: paymentMethod.id,
      });

      if (result.error) throw new Error(result.error.message);

      await onboardingService.confirmBilling(sessionToken, result.setupIntent.id, subscriptionLines);
      navigate("/onboard/final_attestation");
    } catch (err) {
      console.error("Payment error:", err);
      alert(err.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout>
      <motion.div
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
          <MDTypography variant="h3" fontWeight="bold" mb={1}>
            {claimMeta?.claim_type === "build_device" ? "Build + Device" : claimMeta?.claim_type === "device" ? "Device" : "Build"} Billing Information
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={4}>
          Enter your payment details to activate your build.
        </MDTypography>
      </motion.div>

      <MDBox mb={4}>
        <MDTypography variant="caption" fontWeight="bold" color="info" textTransform="uppercase" mb={1} sx={{ display: "block" }}>
          CARD DETAILS
        </MDTypography>
        <MDBox
          id="card-element"
          p={2}
          sx={{
            background: darkMode ? "rgba(2, 6, 23, 0.45)" : "rgba(255, 255, 255, 0.7)",
            borderRadius: "12px",
            border: `1px solid ${t.borderSubtle}`,
            transition: "all 0.3s ease",
            "&:focus-within": {
              borderColor: "info.main",
              boxShadow: "0 0 0 2px rgba(26, 115, 232, 0.15)",
            },
          }}
        />
      </MDBox>

      <MDBox mb={3}>
        <MDTypography variant="caption" color="text" sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          💳 Secured by Stripe. Your data is encrypted and safe.
        </MDTypography>
      </MDBox>

      <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
        <MDButton
          variant="gradient"
          color="info"
          fullWidth
          onClick={handlePayment}
          disabled={loading || !stripe}
          size="large"
          sx={{ borderRadius: "12px", py: 1.5 }}
        >
          {loading ? (
            <MDBox display="flex" alignItems="center">
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Processing Payment...
            </MDBox>
          ) : (
            "Confirm Payment Method"
          )}
        </MDButton>
      </motion.div>
    </OnboardingLayout>
  );
};

export default BillingPage;
